import { AssetDto, AssetImageTypeDataDto } from '@nusign/picflow-backend-types';
import { calcAssetSizeGroup } from '../../lib/data-units';
import { exifKeys, getExifValue } from '../../lib/extract-exif';
import { AssetUserFeedbackMap } from '../interfaces/asset-user-feedback-map';
import { Filter } from '../interfaces/filter.interface';

const feedbackFilterKeys = ['status', 'likes', 'color_label', 'comments', 'annotations'];

export const filterAssetForPeople = (
  userIds: string[],
  asset: AssetDto,
  assetUserFeedbackMap: AssetUserFeedbackMap,
  filters: Filter[]
) => {
  const feedbackFilters = filters.filter(filter => feedbackFilterKeys.includes(filter.key));
  if (feedbackFilters.length) {
    // People filter and other filters so we want to show assets that have been interacted with by these people in specific ways
    return feedbackFilters.some(filter => {
      if (filter.key === 'status') return userIds.includes(assetUserFeedbackMap[asset.id]?.activeStatus);
      if (filter.key === 'color_label') return userIds.includes(assetUserFeedbackMap[asset.id]?.activeColorLabel);
      if (filter.key === 'likes') return userIds.some(id => assetUserFeedbackMap[asset.id]?.liked.includes(id));
      if (filter.key === 'comments') return userIds.some(id => assetUserFeedbackMap[asset.id]?.commented.includes(id));
      if (filter.key === 'annotations')
        return userIds.some(id => assetUserFeedbackMap[asset.id]?.annotatated.includes(id));
      return false;
    });
  }
  return filterAssetForPeopleWithoutFeedback(userIds, asset, assetUserFeedbackMap);
};

export const filterAssetForPeopleWithoutFeedback = (
  userIds: string[],
  asset: AssetDto,
  assetUserFeedbackMap: AssetUserFeedbackMap
) => {
  // Only people filter so we want to show assets that have been interacted with by these people
  if (userIds.includes(assetUserFeedbackMap[asset.id]?.activeStatus)) return true;
  if (userIds.includes(assetUserFeedbackMap[asset.id]?.activeColorLabel)) return true;
  if (userIds.some(id => assetUserFeedbackMap[asset.id]?.liked.includes(id))) return true;
  if (userIds.some(id => assetUserFeedbackMap[asset.id]?.commented.includes(id))) return true;
  if (userIds.some(id => assetUserFeedbackMap[asset.id]?.annotatated.includes(id))) return true;
  return false;
};

export const filterAssets = (
  assets: AssetDto[],
  assetUserFeedbackMap: AssetUserFeedbackMap,
  filters: Filter[],
  searchTerm: string,
  exactMatch = false,
  unseenActivityMap?: Map<string, number>
) => {
  const userIds = filters.find(f => f.key === 'people')?.values as string[];

  assets = assets.filter(asset => {
    const personFilterOk = userIds ? filterAssetForPeople(userIds, asset, assetUserFeedbackMap, filters) : true;
    if (!personFilterOk) return false;
    return !filters.some(filter => {
      if (filter.key === 'people') return false;
      if (feedbackFilterKeys.includes(filter.key)) {
        return singleFilterWithMap(filter, asset.id, assetUserFeedbackMap);
      }
      return singleFilter(filter, asset, unseenActivityMap);
    });
  });

  if (searchTerm) {
    if (exactMatch) {
      assets = assets.filter(asset => {
        if (asset.version_data.asset_name.toLowerCase() === searchTerm.toLowerCase()) return true;
        if (asset.version_data.asset_name.toLowerCase().split('.')[0] === searchTerm.toLowerCase()) return true;
        return false;
      });
    } else {
      // Search term
      const terms = searchTerm.split(' ');
      assets = assets.filter(asset => {
        return !!terms.find(term => asset.version_data.asset_name.toLowerCase().includes(term.toLowerCase()));
      });
    }
  }
  return assets;
};

const singleFilterWithMap = (filter: Filter, assetId: string, assetUserFeedbackMap: AssetUserFeedbackMap): boolean => {
  if (filter.key === 'comments') {
    if (filter.values.includes('with') && assetUserFeedbackMap[assetId]?.commented.length) return false;
    if (filter.values.includes('without') && !assetUserFeedbackMap[assetId]?.commented.length) return false;
    return true;
  }

  if (filter.key === 'annotations') {
    if (filter.values.includes('with') && assetUserFeedbackMap[assetId]?.annotatated.length) return false;
    if (filter.values.includes('without') && !assetUserFeedbackMap[assetId]?.annotatated.length) return false;
    return true;
  }

  if (filter.key === 'likes') {
    if (filter.values.includes('with') && assetUserFeedbackMap[assetId]?.liked.length) return false;
    if (filter.values.includes('without') && !assetUserFeedbackMap[assetId]?.liked.length) return false;
    return true;
  }

  if (filter.key === 'status') {
    return !filter.values.every((value: string) => {
      if (value === 'no_status') {
        return !Object.values(assetUserFeedbackMap[assetId]?.status || {}).filter(Boolean).length;
      }
      return assetUserFeedbackMap[assetId]?.status[value];
    });
  }

  if (filter.key === 'color_label') {
    return !filter.values.every((value: string) => {
      if (value === 'null') {
        return !Object.values(assetUserFeedbackMap[assetId]?.color_label || {}).filter(Boolean).length;
      }
      return assetUserFeedbackMap[assetId]?.color_label[value];
    });
  }

  console.warn('Filter not implemented', filter);
};

export const singleFilter = (filter: Filter, asset: AssetDto, unseenActivityMap?: Map<string, number>): boolean => {
  if (filter.key === 'tags') {
    return !filter.values.some(value => {
      return asset.tags.some(tag => tag.id === value);
    });
  }

  if (filter.key === 'unseen_activity') {
    if (!unseenActivityMap) {
      console.warn('Activity map is required for unseenActivity filter');
    } else {
      if (filter.values.includes('with') && unseenActivityMap.get(asset.id) > 0) return false;
      if (filter.values.includes('without') && unseenActivityMap.get(asset.id) === 0) return false;
    }
  }

  if (filter.key === 'size') {
    const size = calcAssetSizeGroup(BigInt(asset.version_data.size));
    return !filter.values.includes(size);
  }

  let value = asset[filter.key];
  const data = <AssetImageTypeDataDto>asset.version_data?.data;
  if (exifKeys.includes(filter.key)) value = getExifValue(data?.exif, filter.key);
  if (value === undefined) value = null;

  if (typeof value !== 'string') value = JSON.stringify(value);

  const resp = !filter.values
    .map(v => {
      return typeof v !== 'string' ? JSON.stringify(v) : v;
    })
    .find(v => v === value);

  return resp;
};
